// OWNER: FRONTEND-ADMIN. CSV import for a campaign (SPEC §9, §15.5): file → preflight → POST in ≤ 5,000-row chunks →
// ImportReport table with the rejected rows downloadable as CSV.
import { useState } from 'preact/hooks';
import type { ImportReport } from '../../../src/shared/api';
import { errorMessage } from '../api';
import { ErrorBanner, fmtDateTime } from '../components/ui';
import { adminApi } from './endpoints';
import { MAX_IMPORT_ROWS, mapImportHeader, mergeImportReports, planImport, rejectedRowsCsv, type CsvPlan, type HeaderMap } from './csv';
import { groupImportFlags, rejectedCounts } from './imports';
import { Progress, Stat, downloadText, useLoad } from './ui';

interface Plan extends CsvPlan { filename: string; map: HeaderMap }

export function ImportPanel(props: { campaignId: number; campaignName: string; onImported: () => void }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [report, setReport] = useState<(ImportReport & { importIds?: number[] }) | null>(null);
  const [fileKey, setFileKey] = useState(0); // remounts the file input so the same file can be picked again
  const history = useLoad(() => adminApi.imports(props.campaignId), [props.campaignId]);

  const onFile = async (file: File | undefined) => {
    setPlan(null); setReport(null); setError(null);
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name) && file.type && !/csv|text\/plain/.test(file.type)) {
      setError(`“${file.name}” doesn't look like a CSV file. Export the sheet as CSV (UTF-8) and pick that file.`);
      return;
    }
    try {
      const text = await file.text();
      const p = planImport(text);
      setPlan({ ...p, filename: file.name, map: mapImportHeader(p.header) });
    } catch (e) {
      setError(`Couldn't read “${file.name}”: ${errorMessage(e)}`);
    }
  };

  const run = async () => {
    if (!plan) return;
    setBusy(true); setError(null); setDone(0);
    const reports: ImportReport[] = [];
    try {
      for (let k = 0; k < plan.chunks.length; k++) {
        const name = plan.chunks.length > 1 ? `${plan.filename} (part ${k + 1} of ${plan.chunks.length})` : plan.filename;
        try {
          reports.push(await adminApi.importCsv(props.campaignId, plan.chunks[k], name));
        } catch (e) {
          const first = k * plan.chunkSize + 1;
          const last = Math.min(plan.dataRows, (k + 1) * plan.chunkSize);
          throw new Error(`${reports.length ? `Rows 1–${first - 1} were imported. ` : ''}Rows ${first}–${last} failed: ${errorMessage(e)}`);
        }
        setDone(k + 1);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      if (reports.length) {
        setReport(reports.length === 1 ? reports[0] : mergeImportReports(reports, plan.chunkSize));
        props.onImported();
        void history.reload();
      }
      setBusy(false);
      setPlan(null);
      setFileKey((k) => k + 1);
    }
  };

  const openPast = async (importId: number) => {
    setError(null);
    try { setReport(await adminApi.importReport(props.campaignId, importId)); } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div class="stack" style="gap:12px">
      <div class="card stack">
        <h3>Import leads from CSV</h3>
        <div class="adm-sub">
          Required columns: business name, phone, state/province and <b>source_url</b> (where the row came from; no source, no row).
          Optional: country, city, postal, website, gbp_url, email + email_source_url, dm_name, dm_role, niche, observation + observation_source_url.
          Up to {MAX_IMPORT_ROWS.toLocaleString()} rows per request; bigger files are sent in parts automatically.
        </div>
        <div class="row">
          <input key={fileKey} type="file" accept=".csv,text/csv" disabled={busy} onChange={(e) => void onFile((e.target as HTMLInputElement).files?.[0])} />
        </div>
        <ErrorBanner error={error} />
        {plan ? (
          <div class="stack">
            <div class="row" style="flex-wrap:wrap;gap:10px">
              <Stat label="data rows" value={plan.dataRows.toLocaleString()} />
              <Stat label={plan.chunks.length === 1 ? 'request' : 'requests'} value={plan.chunks.length} />
              <Stat label="columns mapped" value={`${plan.map.mapped.length}/${plan.header.length}`} />
            </div>
            <table>
              <thead><tr><th>Field</th><th>Your column</th></tr></thead>
              <tbody>{plan.map.mapped.map((m) => <tr><td>{m.label}</td><td class="mono">{m.column}</td></tr>)}</tbody>
            </table>
            {plan.map.missingRequired.length ? (
              <div class="chip chip-bad" style="display:block;padding:8px 10px">
                Missing required column{plan.map.missingRequired.length > 1 ? 's' : ''}: {plan.map.missingRequired.join(', ')}. Every row would be rejected. Add the column and pick the file again.
              </div>
            ) : null}
            {plan.map.unknown.length ? <div class="adm-sub">Ignored columns: {plan.map.unknown.join(', ')}</div> : null}
            <div class="row">
              <button class="btn btn-primary" disabled={busy || plan.map.missingRequired.length > 0} onClick={run}>
                {busy ? 'Importing…' : `Import ${plan.dataRows.toLocaleString()} rows into ${props.campaignName}`}
              </button>
              <button class="btn" disabled={busy} onClick={() => { setPlan(null); setFileKey((k) => k + 1); }}>Cancel</button>
            </div>
            {busy && plan.chunks.length > 1 ? <div class="stack"><Progress done={done} total={plan.chunks.length} /><span class="adm-sub">Part {Math.min(done + 1, plan.chunks.length)} of {plan.chunks.length}…</span></div> : null}
          </div>
        ) : null}
      </div>

      {report ? <ImportReportView report={report} campaignName={props.campaignName} /> : null}

      <div class="card stack">
        <h3>Past imports</h3>
        <ErrorBanner error={history.error} />
        {history.data && history.data.length === 0 ? <div class="muted">No imports yet.</div> : null}
        {history.data && history.data.length > 0 ? (
          <div class="adm-table-wrap">
            <table>
              <thead><tr><th>When</th><th>File</th><th>By</th><th class="num">Rows</th><th class="num">Imported</th><th class="num">Rejected</th><th /></tr></thead>
              <tbody>
                {history.data.map((h) => (
                  <tr>
                    <td>{fmtDateTime(h.createdAt)}</td><td>{h.filename ?? '—'}</td><td>{h.createdByName ?? '—'}</td>
                    <td class="num">{h.rowsTotal}</td><td class="num">{h.rowsImported}</td><td class="num">{h.rowsRejected}</td>
                    <td><button class="btn btn-sm" onClick={() => void openPast(h.id)}>Report</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ImportReportView(props: { report: ImportReport; campaignName: string }) {
  const r = props.report;
  const flags = groupImportFlags(r.flagged);
  const slug = props.campaignName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const rejected = r.rejected ?? [];
  const counts = rejectedCounts(r);
  return (
    <div class="card stack">
      <h3>Import report</h3>
      <div class="row" style="flex-wrap:wrap;gap:10px">
        <Stat label="rows in file" value={r.total} />
        <Stat label="added to campaign" value={r.imported} />
        <Stat label="already known businesses" value={r.reusedBusinesses} />
        <Stat label="rejected" value={counts.total} />
        <Stat label="flagged" value={(r.flagged ?? []).length} />
        <Stat label="line-type lookups queued" value={r.lookupsQueued} />
      </div>
      {flags.length ? (
        <div class="stack">
          <b>Flagged rows (imported; “held” rows stay blocked by the dial gate until fixed, “check” rows can be dialled but need a look)</b>
          <table>
            <thead><tr><th>Flag</th><th /><th>What it means</th><th class="num">Rows</th><th>Row numbers</th></tr></thead>
            <tbody>{flags.map((g) => (
              <tr><td class="mono">{g.flag}</td>
                <td>{g.info ? <span class={`chip ${g.info.effect === 'held' ? 'chip-bad' : 'chip-warn'}`}>{g.info.effect}</span> : null}</td>
                <td>{g.info?.text ?? 'A flag this page doesn’t know yet; check the rows.'}</td><td class="num">{g.rows.length}</td>
                <td class="mono" style="font-size:12px">{g.rows.slice(0, 30).join(', ')}{g.rows.length > 30 ? ` … +${g.rows.length - 30}` : ''}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      {rejected.length ? (
        <div class="stack">
          <div class="row" style="justify-content:space-between">
            <b>Rejected rows (not imported)</b>
            <button class="btn btn-sm btn-ink" onClick={() => downloadText(`${slug || 'campaign'}-rejected-rows.csv`, rejectedRowsCsv(rejected))}>
              Download {rejected.length} rejected rows (CSV)
            </button>
          </div>
          {counts.truncated ? (
            <div class="chip chip-warn" style="display:block;padding:6px 10px;white-space:normal">
              This saved report kept the first {counts.shown.toLocaleString()} of {counts.total.toLocaleString()} rejected rows (cells shortened). The full list was only in the
              report shown right after the import. Importing the original file again rebuilds it: rows already in the campaign are kept as they are (flagged already_in_campaign).
            </div>
          ) : null}
          <div class="adm-table-wrap" style="max-height:360px">
            <table>
              <thead><tr><th class="num">Row</th><th>Reason</th><th>Name</th><th>Phone</th><th>State</th><th>Source</th></tr></thead>
              <tbody>{rejected.slice(0, 300).map((e) => {
                const raw = e.raw ?? {};
                const pick = (...keys: string[]) => { for (const k of Object.keys(raw)) if (keys.includes(k.trim().toLowerCase().replace(/[\s-]+/g, '_'))) return raw[k]; return ''; };
                return (
                  <tr><td class="num">{e.row}</td><td>{e.reason}</td><td>{pick('name', 'business', 'business_name', 'company')}</td>
                    <td class="mono">{pick('phone', 'phone_number', 'main_phone', 'telephone', 'phone_e164')}</td>
                    <td>{pick('state', 'province', 'state_prov', 'region')}</td>
                    <td style="max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{pick('source_url', 'source')}</td></tr>
                );
              })}</tbody>
            </table>
          </div>
          {rejected.length > 300 ? <div class="adm-sub">Showing the first 300; the download has all {rejected.length}.</div> : null}
        </div>
      ) : null}
    </div>
  );
}
